import styled, { type Colors, type Radii, css } from 'styled-components';
import type { ReactNode } from 'react';

import { Label, type LabelProps } from '@yoweb/ui/components/typography';
import { pxToRem } from '@yoweb/ui/styles/utils';
import { getColor, getSpace, getRadii } from '@yoweb/ui/styles/utils/theme';

export type BadgeProps = {
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  children: ReactNode;
  type?: 'stroke' | 'fill' | 'positive';
  backgroundColor?: keyof Colors;
  borderRadius?: keyof Radii;
  // Label size
  size?: LabelProps['size'];
  mode?: 'dark' | 'light';
} & DataTestId;

export const Badge = ({
  as = 'div',
  className,
  children,
  type = 'stroke',
  backgroundColor,
  size = 'sm',
  mode = 'light',
  borderRadius = 'large',
  ...rest
}: BadgeProps) => (
  <BadgeContainer
    className={className}
    type={type}
    backgroundColor={backgroundColor}
    mode={mode}
    borderRadius={borderRadius}
    {...rest}
  >
    <Label as={as} size={size}>
      {children}
    </Label>
  </BadgeContainer>
);

const BadgeContainer = styled.div<{
  type: string;
  borderRadius: keyof Radii;
  backgroundColor?: keyof Colors;
  mode: 'dark' | 'light';
}>`
  border-radius: ${getRadii('large')};
  justify-self: flex-start;
  padding: ${pxToRem(6)} ${getSpace('small3')};

  ${({ borderRadius }) => css`
    border-radius: ${getRadii(borderRadius)};
  `}

  ${({ type, backgroundColor }) => {
    if (type === 'stroke') {
      return css`
        border: 1px solid ${getColor('baseA700')};
      `;
    }

    if (type === 'fill') {
      return css`
        background-color: ${getColor(backgroundColor || 'base100')};
      `;
    }

    if (type === 'positive') {
      return css`
        background-color: ${getColor('success')};
        color: ${getColor('base000')};
      `;
    }
  }}

  ${({ mode }) =>
    mode === 'dark' &&
    css`
      color: ${getColor('base000')};
    `}
`;

export default Badge;
